import {Container, Tabs} from '@mantine/core';
import {FC, useMemo} from 'react';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {JobStatusTypes, JobTypes} from '../api/generated/enums';
import {JobDetailDto, JobSummaryDto} from '../api/generated/index.defs';
import {BreadcrumbPart, PageTitle} from '../components/page-title';
import {ROUTES} from '../routes';
import {JobArchive} from './job-archive/job-archive';
import {JobCertificate, NonMmJobClosedArchived} from './job-certificate/job-certificate';
import {JobUpdate} from './job-create-update';
import {JobEvaluation} from './job-evaluation/job-evaluation';
import {getColorForStatus} from './job-highlight';
import {JobListing, JobListingProps} from './job-listing';
import {useJobStore} from './job-store';
import classes from './job-tabs.module.css';
import {useJobCrumbs} from './hooks/use-job-crumbs.ts';
import {useAsync} from 'react-use';

type TabConfig = {
  value: string;
  label: string;
  disabled: boolean;
  Component: FC;
};

const updateTabStatus = (
  status: string | undefined,
  tabs: TabConfig[],
): TabConfig[] => {
  return tabs.map((tab) => {
    switch (status) {
      case JobStatusTypes.Archiving:
        return {
          ...tab,
          disabled: tab.value === JobStatusTypes['Closed - Archived'],
        };
      case JobStatusTypes['Closed - Archived']:
        return {
          ...tab,
          disabled: false,
        };
      default:
        return tab;
    }
  });
};

type GetTabs = (job: JobDetailDto | undefined) => TabConfig[];

const withoutColumns = [
  'assetNumber',
  'reference1',
] satisfies (keyof JobSummaryDto)[];

const getTabs: GetTabs = (job) => {
  const initialValues: JobListingProps['initialValues'] = {
    createdDate: job?.createdDate,
    serialNumber: job?.serialNumber,
    jobType: job?.jobType,
    isHistory: true,
  };

  const initialTabs = [
    {
      disabled: false,
      value: 'serviceHistory',
      label: 'Service History',
      Component: () => (
        <JobListing
          initialValues={initialValues}
          withoutColumns={withoutColumns}
          withoutFilters
          withoutActions
        />
      ),
    },
    {
      disabled: false,
      value: 'jobDetails',
      label: 'Job Details',
      Component: JobUpdate,
    },
    {
      disabled: false,
      value: JobStatusTypes.Evaluation,
      label: 'Evaluation',
      Component: JobEvaluation,
    },
    {
      disabled: true,
      value: JobStatusTypes.Archiving,
      label: 'Archiving',
      Component: JobArchive,
    },
    {...(job?.jobType === JobTypes["Non-M&M"] ? {
        disabled: true,
        value: JobStatusTypes['Closed - Archived'],
        label: 'Repair Summary',
        Component: NonMmJobClosedArchived,
      } : {
        disabled: true,
        value: JobStatusTypes['Closed - Archived'],
        label: 'Certificate',
        Component: JobCertificate,
      })}

  ] as const satisfies TabConfig[];

  return updateTabStatus(job?.status, initialTabs);
};

export const JobTabs: FC = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const {
    job,
    isLoading,
    fetchJob,
    clearJob,
    fetchReplacedParts,
    clearReplacedParts,
  } = useJobStore((state) => ({
    job: state.job ?? undefined,
    isLoading: state.isJobLoading,
    fetchJob: state.fetchJob,
    clearJob: state.clearJob,
    fetchReplacedParts: state.fetchReplacedParts,
    clearReplacedParts: state.clearReplacedParts,
  }));

  const breadcrumbs = useMemo<BreadcrumbPart[]>(() => {
    const jobId = `${id}`;
    const {
      levelOfServiceDescription,
      partNumber,
      assetNumber,
      serialNumber,
      status,
      quoteStatus,
      reviewStatus,
      clientName,
      isRepairable,
    } = useJobCrumbs(job);

    if (isLoading) {
      return [
        {title: 'All Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
        {title: jobId, copiable: true},
        {title: 'Loading...', isLoading: true},
      ];
    }

    let crumbs: BreadcrumbPart[] = [
      {title: 'All Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
      {title: jobId, copiable: true, description: 'Job ID'},
      {
        title: status,
        description: 'Job Status',
        color: getColorForStatus('status', status),
      },
      {title: levelOfServiceDescription, description: 'Level of Service'},
      {title: serialNumber, copiable: true, description: 'Serial Number'},
      {title: partNumber, copiable: true, description: 'Part Number'},
      {title: clientName, description: 'Client Name'},
      {title: assetNumber, description: 'Asset Number'},
      {
        title: isRepairable,
        color: getColorForStatus(
          'evaluationIsRepairable',
          isRepairable.toLowerCase(),
        ),
        description: 'Repairable',
      },
      {
        title: quoteStatus,
        color: getColorForStatus('evaluationQuoteStatus', quoteStatus),
        description: 'Quote Status',
      },
      {
        title: reviewStatus,
        color: getColorForStatus('reviewStatus', reviewStatus),
        description: 'Review Status',
      },
      {
        title: job?.partnerSiteName ?? '',
        description: 'Partner Site',
      },
    ];

    if (job?.isNewRevisionAvailable) {
      crumbs = [
        ...crumbs,
        {
          title: 'New Revision Available',
          color: 'orange',
        },
      ];
    }

    return crumbs;
  }, [id, isLoading, job]);

  useAsync(async () => {
    await fetchJob(id);
    await fetchReplacedParts(id);
    return () => {
      clearJob();
      clearReplacedParts();
    };
  }, [id, fetchJob, clearJob, fetchReplacedParts, clearReplacedParts]);

  const tabs = useMemo(() => {
    return getTabs(job);
  }, [job]);

  return (
    <Tabs variant='unstyled' classNames={classes} defaultValue={tabs[0].value}>
      <Container pt={9} px={0} size='97%' h={55} mb={9}>
        <PageTitle crumbs={breadcrumbs} />
        <Tabs.List grow>
          {tabs.map((tab, index) => (
            <Tabs.Tab
              key={index}
              value={tab.value}
              children={tab.label}
              disabled={tab.disabled}
            />
          ))}
        </Tabs.List>
      </Container>

      {tabs.map(({value, Component}, index) => (
        <Tabs.Panel key={index} value={value}>
          <Component />
        </Tabs.Panel>
      ))}
    </Tabs>
  );
};